import { useState } from 'react';
import { Btn, Icon, IconBtn, type IconName } from '../components/ui';
import { useTheme } from '../theme';
import { AuthView, type AuthMode } from './Auth';

// Sitio público del portal (fase 'anon'): landing de marketing para carriers y
// operadores + las tres pantallas de acceso (entrar, crear cuenta, recuperar).
// La conmutación es estado local — la URL (y su ?v= de secciones) queda
// intacta para no romper deep-links del portal autenticado.

export type PublicView = 'landing' | AuthMode;

interface PublicSiteProps {
  onSignIn: (identifier: string, password: string) => Promise<void>;
  onSignUp: (email: string, password: string, displayName: string) => Promise<void>;
  busy: boolean;
  error: string | null;
  notice: string | null;
  dismiss: () => void;
}

export function PublicSite(props: PublicSiteProps) {
  const [view, setView] = useState<PublicView>('landing');
  const [theme, toggleTheme] = useTheme();
  const go = (next: PublicView) => { props.dismiss(); setView(next); window.scrollTo({ top: 0 }); };

  return <div className="page">
    <SiteHeader theme={theme} onToggleTheme={toggleTheme}
      onBrand={() => go('landing')} onLogin={() => go('login')} onRegister={() => go('register')} />
    {view === 'landing'
      ? <Landing onLogin={() => go('login')} onRegister={() => go('register')} />
      : <AuthView key={view} mode={view} onMode={go}
          onSignIn={props.onSignIn} onSignUp={props.onSignUp}
          busy={props.busy} error={props.error} notice={props.notice} dismiss={props.dismiss} />}
    <SiteFooter onLogin={() => go('login')} onRegister={() => go('register')} />
  </div>;
}

/** Barra superior del sitio público: misma marca que el riel del portal. */
function SiteHeader({ theme, onToggleTheme, onBrand, onLogin, onRegister }: {
  theme: 'light' | 'dark'; onToggleTheme(): void; onBrand(): void; onLogin(): void; onRegister(): void;
}) {
  return <header className="site-nav">
    <button type="button" className="site-brand" onClick={onBrand} aria-label="Cargados.mx — inicio">
      <span className="brand-mark">◆</span><span className="name">Cargados.mx</span>
    </button>
    <span className="spacer" />
    <IconBtn label={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'} icon={theme === 'dark' ? 'sun' : 'moon'} onClick={onToggleTheme} />
    <Btn tone="ghost" small onClick={onLogin}>Entrar</Btn>
    <Btn small onClick={onRegister}>Crear cuenta</Btn>
  </header>;
}

function SiteFooter({ onLogin, onRegister }: { onLogin(): void; onRegister(): void }) {
  return <footer className="site-foot">
    <div className="row">
      <span className="brand-mark" aria-hidden="true">◆</span>
      <div className="who">
        <div className="name">Cargados.mx</div>
        <div className="tiny muted">Corredor Ensenada ⇄ Los Ángeles</div>
      </div>
    </div>
    <span className="spacer" />
    <div className="actions">
      <button type="button" className="foot-link" onClick={onLogin}>Entrar</button>
      <button type="button" className="foot-link" onClick={onRegister}>Crear cuenta</button>
    </div>
  </footer>;
}

// ---------- Contenido de la landing ----------

const FEATURES: { icon: IconName; title: string; desc: string }[] = [
  { icon: 'route', title: 'Despacho de viajes', desc: 'Asigna chofer, paradas, carga y unidades. La oficina ve en qué parada va cada viaje sin hacer una sola llamada.' },
  { icon: 'truck', title: 'Flota y mantenimiento', desc: 'Tractos, cajas y reefers con placas MX/US, contador y próximo servicio. Una unidad en taller no se puede asignar a un viaje.' },
  { icon: 'doc', title: 'Documentos con vencimiento', desc: 'Seguros, registros, fianzas, visas y permisos con foto y verificación; los avisos se prenden antes de que venzan.' },
  { icon: 'person', title: 'Equipo con roles', desc: 'Oficina despacha y administra; el chofer ve lo suyo en la app. Cada miembro entra con correo verificado y aprobación del administrador.' },
  { icon: 'building', title: 'Directorio de lugares', desc: 'Accesos, maniobra, horarios y tiempos reales de los lugares de la ruta, alimentado por todas las empresas de la plataforma.' },
  { icon: 'clipboard', title: 'Bitácora', desc: 'Solicitudes, autorizaciones y cambios quedan registrados: siempre se sabe quién autorizó qué.' },
];

const DRIVER_FEATURES: { icon: IconName; title: string; desc: string }[] = [
  { icon: 'route', title: 'Mi ruta', desc: 'El viaje asignado con sus paradas, piezas y notas, hecho para consultarse en el camino.' },
  { icon: 'pin', title: 'Directorio real de la ruta', desc: 'Entrada para camiones, claves y notas de maniobra — escritas por quienes ya estuvieron en ese lugar.' },
  { icon: 'doc', title: 'Sus documentos', desc: 'Visa B1, licencia, I-94 y apto médico con foto y vencimiento a la vista.' },
  { icon: 'bell', title: 'Avisos', desc: 'Viaje nuevo, parada actualizada o documento por vencer: le llega como notificación.' },
];

const TRUST_POINTS = [
  'Cada cuenta nueva queda pendiente hasta que un administrador la autoriza.',
  'Los datos privados de tu empresa — lugares, notas, viajes y documentos — solo los ven sus miembros.',
  'Correo verificado con código y contraseña de 12 caracteres mínimo: nadie entra sin identificarse.',
];

function Feature({ icon, title, desc }: { icon: IconName; title: string; desc: string }) {
  return <div className="feature">
    <span className="feature-ic"><Icon name={icon} size={21} /></span>
    <h3>{title}</h3>
    <p>{desc}</p>
  </div>;
}

function Landing({ onLogin, onRegister }: { onLogin(): void; onRegister(): void }) {
  return <main className="landing">
    <section className="hero">
      <span className="tag">PLATAFORMA PARA TRANSPORTE DE CARGA</span>
      <h1>La oficina, la flota y el chofer en la misma página.</h1>
      <p className="lede">
        Cargados.mx es el TMS de las empresas del corredor: despacha viajes con paradas y unidades,
        controla los documentos que vencen y mantiene a la oficina y al chofer en la misma página.
        Tu chofer lo recibe todo en la app.
      </p>
      <div className="hero-cta">
        <Btn onClick={onRegister}>Crear cuenta<Icon name="arrowRight" size={17} /></Btn>
        <Btn tone="ghost" onClick={onLogin}>Entrar</Btn>
      </div>
      <p className="hero-note">Acceso por aprobación — Cargados.mx es una comunidad verificada.</p>
    </section>

    <section className="lp-section" aria-label="Para tu empresa">
      <span className="tag">PARA TU EMPRESA</span>
      <h2 className="lp-h2">El TMS que la oficina usa todos los días</h2>
      <p className="lp-desc">
        Del viaje al documento que está por vencer: lo que la operación necesita para
        cruzar carga sin perseguir papeles ni mensajes.
      </p>
      <div className="lp-grid">
        {FEATURES.map(f => <Feature key={f.title} {...f} />)}
      </div>
    </section>

    <section className="lp-section" aria-label="Para operadores">
      <span className="tag">PARA OPERADORES</span>
      <h2 className="lp-h2">La app del chofer, incluida</h2>
      <p className="lp-desc">
        iOS y Android con la misma cuenta: su ruta, sus documentos y el directorio
        de lugares de la ruta — la información que de verdad necesita en carretera.
      </p>
      <div className="lp-grid">
        {DRIVER_FEATURES.map(f => <Feature key={f.title} {...f} />)}
      </div>
    </section>

    <section className="lp-section" aria-label="Acceso por aprobación">
      <div className="lp-panel">
        <div className="lp-panel-main">
          <span className="feature-ic"><Icon name="shield" size={22} /></span>
          <h2 className="lp-h2">Acceso por aprobación</h2>
          <p className="lp-desc">
            Cargados.mx no es un directorio público. La intel de los lugares solo
            vale si quien la escribe es de confianza — por eso el registro es
            abierto pero el acceso lo autoriza un administrador.
          </p>
        </div>
        <ul className="checklist">
          {TRUST_POINTS.map(point => <li key={point}><Icon name="check" size={17} /><span>{point}</span></li>)}
        </ul>
      </div>
    </section>

    <section className="lp-cta" aria-label="Crear cuenta">
      <h2 className="lp-h2">¿Tu empresa cruza carga?</h2>
      <p className="lp-desc">Crea tu cuenta, verifica tu correo y un administrador autoriza tu acceso.</p>
      <div className="hero-cta">
        <Btn onClick={onRegister}>Crear cuenta<Icon name="arrowRight" size={17} /></Btn>
        <Btn tone="ghost" onClick={onLogin}>Ya tengo cuenta</Btn>
      </div>
    </section>
  </main>;
}
